import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import toast from 'react-hot-toast';
import { reportsApi } from '../../services/api';

const PIE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6'];

function generateEmptyDaily(days) {
  const result = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    result.push({
      date: d.toLocaleDateString('az-AZ', { day: '2-digit', month: '2-digit' }),
      opens: 0,
    });
  }
  return result;
}

const EMPTY_PIE = [
  { name: 'İnternet', value: 0 },
  { name: 'Bluetooth', value: 0 },
  { name: 'QR Kod', value: 0 },
];

const EMPTY_STATS = {
  totalOpens:       0,
  newUsers:         0,
  activeDeliveries: 0,
  announcements:    0,
};

function getDateRange(preset) {
  const now = new Date();
  const to = now.toISOString().slice(0, 10);
  let from;
  if (preset === 'week') {
    const d = new Date(now); d.setDate(d.getDate() - 7);
    from = d.toISOString().slice(0, 10);
  } else if (preset === 'month') {
    const d = new Date(now); d.setMonth(d.getMonth() - 1);
    from = d.toISOString().slice(0, 10);
  } else if (preset === '3months') {
    const d = new Date(now); d.setMonth(d.getMonth() - 3);
    from = d.toISOString().slice(0, 10);
  }
  return { from, to };
}

function StatCard({ icon, label, value, color }) {
  return (
    <div style={{
      background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12,
      padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 14,
    }}>
      <div style={{
        width: 44, height: 44, borderRadius: 10,
        background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 20, flexShrink: 0,
      }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--text)', lineHeight: 1.1 }}>{value ?? '—'}</div>
        <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{label}</div>
      </div>
    </div>
  );
}

function exportCSV(dailyData, pieData, stats) {
  const rows = [
    ['Tarix', 'Açılma sayı'],
    ...dailyData.map(d => [d.date, d.opens]),
    [],
    ['Metod', 'Say'],
    ...pieData.map(p => [p.name, p.value]),
    [],
    ['Ümumi açılma', stats.totalOpens],
    ['Yeni istifadəçilər', stats.newUsers],
    ['Aktiv çatdırılmalar', stats.activeDeliveries],
    ['Elanlar', stats.announcements],
  ];
  const csv = rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `baku-gate-hesabat-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function exportJSON(dailyData, pieData, stats) {
  const data = { stats, dailyOpens: dailyData, opensByMethod: pieData, generatedAt: new Date().toISOString() };
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `baku-gate-hesabat-${new Date().toISOString().slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Reports() {
  const [preset,     setPreset]     = useState('week');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo,   setCustomTo]   = useState('');
  const [loading,    setLoading]    = useState(false);
  const [stats,      setStats]      = useState(EMPTY_STATS);
  const [dailyData,  setDailyData]  = useState(() => generateEmptyDaily(7));
  const [pieData,    setPieData]    = useState(EMPTY_PIE);

  const fetchData = useCallback(async (from, to) => {
    setLoading(true);
    try {
      const [barrierStats] = await Promise.allSettled([
        reportsApi.getBarrierStats(from, to),
      ]);

      if (barrierStats.status === 'fulfilled' && barrierStats.value) {
        const d = barrierStats.value;
        if (d.dailyOpens) setDailyData(d.dailyOpens);
        if (d.opensByMethod) setPieData(d.opensByMethod);
        if (d.totalOpens != null) setStats(s => ({ ...s, totalOpens: d.totalOpens }));
        if (d.newUsers   != null) setStats(s => ({ ...s, newUsers:   d.newUsers   }));
      } else {
        const days = preset === 'week' ? 7 : preset === 'month' ? 30 : 90;
        setDailyData(generateEmptyDaily(days));
        setPieData(EMPTY_PIE);
        setStats(EMPTY_STATS);
      }
    } catch {
      const days = preset === 'week' ? 7 : preset === 'month' ? 30 : 90;
      setDailyData(generateEmptyDaily(days));
      setPieData(EMPTY_PIE);
      setStats(EMPTY_STATS);
    } finally {
      setLoading(false);
    }
  }, [preset]);

  useEffect(() => {
    const { from, to } = preset === 'custom'
      ? { from: customFrom, to: customTo }
      : getDateRange(preset);
    if (from && to) fetchData(from, to);
  }, [preset, fetchData]); // eslint-disable-line

  const handleRefresh = () => {
    const { from, to } = preset === 'custom'
      ? { from: customFrom, to: customTo }
      : getDateRange(preset);
    if (preset === 'custom' && (!customFrom || !customTo)) {
      toast.error('Başlanğıc və bitmə tarixi seçin');
      return;
    }
    fetchData(from, to);
  };

  return (
    <div style={{ maxWidth: 960, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: 'var(--text)', marginBottom: 4 }}>Hesabatlar</h1>
        <p style={{ color: 'var(--text3)', fontSize: 13 }}>Sistem statistikası və analitikası</p>
      </div>

      {/* Date range picker */}
      <div style={{ ...S.section, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {[['week','Bu həftə'],['month','Bu ay'],['3months','Son 3 ay'],['custom','Xüsusi']].map(([k,l]) => (
            <button key={k}
              style={{
                padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                cursor: 'pointer', border: '1px solid',
                color:       preset === k ? 'var(--primary)' : 'var(--text3)',
                borderColor: preset === k ? 'var(--primary)' : 'var(--border)',
                background:  preset === k ? 'var(--primary)15' : 'transparent',
                transition: 'all 0.15s',
              }}
              onClick={() => setPreset(k)}>
              {l}
            </button>
          ))}

          {preset === 'custom' && (
            <>
              <input type="date" style={S.inp} value={customFrom}
                onChange={e => setCustomFrom(e.target.value)} />
              <span style={{ color: 'var(--text3)', fontSize: 12 }}>—</span>
              <input type="date" style={S.inp} value={customTo}
                onChange={e => setCustomTo(e.target.value)} />
            </>
          )}

          <button style={{ ...S.btn, marginLeft: 'auto' }} onClick={handleRefresh} disabled={loading}>
            {loading ? '⏳' : '↺ Yenilə'}
          </button>
        </div>
      </div>

      {/* Stats cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 20 }}>
        <StatCard icon="🚧" label="Şlaqbaum açılması"   value={stats.totalOpens}       color="#3b82f6" />
        <StatCard icon="👤" label="Yeni istifadəçilər"  value={stats.newUsers}          color="#10b981" />
        <StatCard icon="📦" label="Aktiv çatdırılmalar" value={stats.activeDeliveries}  color="#f59e0b" />
        <StatCard icon="📢" label="Elanlar"             value={stats.announcements}     color="#8b5cf6" />
      </div>

      {/* Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 16, marginBottom: 20 }}>

        {/* Bar chart */}
        <div style={S.section}>
          <div style={S.sectionTitle}>Günlük Şlaqbaum Açılmaları</div>
          {loading ? (
            <div style={S.chartPlaceholder}>Yüklənir...</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={dailyData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--text3)' }} />
                <YAxis tick={{ fontSize: 10, fill: 'var(--text3)' }} />
                <Tooltip
                  contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8 }}
                  labelStyle={{ color: 'var(--text)', fontSize: 12 }}
                  itemStyle={{ color: 'var(--primary)' }}
                />
                <Bar dataKey="opens" fill="var(--primary)" radius={[4, 4, 0, 0]} name="Açılma" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Pie chart */}
        <div style={S.section}>
          <div style={S.sectionTitle}>Metod üzrə</div>
          {loading ? (
            <div style={S.chartPlaceholder}>Yüklənir...</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={80}
                  paddingAngle={4} dataKey="value">
                  {pieData.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8 }}
                  itemStyle={{ color: 'var(--text)' }}
                  formatter={(v) => [`${v}%`, '']}
                />
                <Legend
                  iconType="circle"
                  formatter={(v) => <span style={{ color: 'var(--text2)', fontSize: 11 }}>{v}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Export */}
      <div style={{ ...S.section, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 2 }}>Hesabatı İxrac et</div>
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>Cari məlumatları fayl olaraq yüklə</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={S.btn} onClick={() => { exportCSV(dailyData, pieData, stats); toast.success('CSV yükləndi'); }}>
            ⬇ CSV İxrac et
          </button>
          <button style={{ ...S.btn, background: '#10b98115', color: '#10b981', border: '1px solid #10b98130' }}
            onClick={() => { exportJSON(dailyData, pieData, stats); toast.success('JSON yükləndi'); }}>
            ⬇ JSON İxrac et
          </button>
        </div>
      </div>

    </div>
  );
}

const S = {
  section:      { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: '18px 20px', marginBottom: 0 },
  sectionTitle: { fontSize: 13, fontWeight: 700, color: 'var(--text2)', marginBottom: 14 },
  chartPlaceholder: { height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text3)', fontSize: 13 },
  inp: { background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 7, padding: '6px 10px', color: 'var(--text)', fontSize: 12, outline: 'none', boxSizing: 'border-box' },
  btn: { background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 16px', fontWeight: 700, cursor: 'pointer', fontSize: 12 },
};
